import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";

import { apiClient } from "../../shared/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Khung điện thoại xem trước Mini App (#18657). Nhân viên xem ngay tại HIS phòng khám của mình
 * đang hiện gì trên app, không phải mở Addy Sites chỉ để nhìn.
 *
 * HAI chế độ, chọn theo cấu hình `addy.sites.sync.mini-app-preview-base`:
 *
 * <ul>
 *   <li><b>Nhúng app thật</b> (khi có địa chỉ) — iframe trỏ vào chính Mini App đang chạy. Đây là
 *       chế độ đúng: khung hiện đúng thứ bệnh nhân thấy, và nó tự đúng theo app mà không ai phải
 *       cập nhật gì ở đây.
 *   <li><b>Bản mô phỏng</b> (khi để trống) — vẽ lại màu theme (`theme.tokens`) và bài viết
 *       (`recentPosts`). Đường lùi cho production: Zalo phục vụ bản đã phát hành từ `zalo.me`,
 *       nơi không cho nhúng iframe và còn đòi đăng nhập Zalo.
 * </ul>
 *
 * <p>Bản mô phỏng có một nhược điểm phải biết: nó chỉ vẽ được những gì có người ngồi dựng, nên mỗi
 * lần Mini App đọc thêm một phần của bundle thì khung này lại sai thêm một phần, và không có gì
 * báo. Đó là lý do chế độ nhúng được ưu tiên ở mọi nơi nhúng được.
 */

/** Đúng bộ token của addy-sites `ThemePreset.tokens`, xem addy-sites `apps/mini-app/src/theme.ts`. */
interface ThemeTokens {
  brand?: string;
  brand2?: string;
  accent?: string;
  bg?: string;
  surface?: string;
  ink?: string;
}

/**
 * Token do phòng khám nhập nên là dữ liệu không tin được. Chỉ nhận màu literal — cùng luật với
 * `isSafeColor` trong Mini App, để bản xem trước không nới lỏng thứ app thật đã siết.
 */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB = /^rgba?\([0-9.,\s%]+\)$/i;

function safeColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const v = value.trim();
  return HEX.test(v) || RGB.test(v) ? v : fallback;
}

interface PreviewPost {
  slug?: string;
  title?: string;
  excerpt?: string | null;
  coverImageUrl?: string | null;
}

interface PreviewBundle {
  tenant?: { clinicName?: string; tagline?: string | null; phone?: string | null };
  theme?: { tokens?: ThemeTokens; primaryColor?: string | null; logoUrl?: string | null };
  recentPosts?: PreviewPost[];
}

interface PreviewResponse {
  available: boolean;
  reason?: string;
  slug?: string;
  bundle?: PreviewBundle;
  /**
   * Địa chỉ để NHÚNG chính Mini App, do máy chủ dựng từ `addy.sites.sync.mini-app-preview-base`.
   * Vắng mặt = chưa khai, và khi đó khung vẽ bản mô phỏng bên dưới.
   */
  previewUrl?: string;
}

const previewApi = () =>
  apiClient.get<PreviewResponse>("/v1/website/miniapp-preview").then((r) => r.data);

/**
 * Khung máy cao BAO NHIÊU thì đo, không đoán.
 *
 * <p>Hai bản trước đều là một hằng số trừ hao: {@code calc(100vh - 400px)} rồi {@code 100vh - 300px}.
 * Cả hai đều sai theo cùng một kiểu. {@code 100vh} là chiều cao CẢ CỬA SỔ, còn khung máy nằm trong
 * {@code <main>} — vùng đã bị topbar ăn mất một đoạn — và bên trên nó còn tiêu đề trang, đầu thẻ,
 * lề. Số trừ hao vì thế phải đoán tổng của năm thứ có thể đổi bất cứ lúc nào, và đoán thụt thì thừa
 * một khoảng trống dưới đáy, đoán dư thì trang phải cuộn.
 *
 * <p>Bản này đo khoảng cách thật từ đỉnh thẻ xuống đáy vùng cuộn, rồi trừ đi phần KHÔNG phải khung
 * máy trong chính thẻ đó ({@code thẻ.offsetHeight - khung.offsetHeight}). Nhờ vậy thêm hay bớt một
 * dòng chú thích, đổi chiều cao topbar hay đổi mức zoom đều tự đúng, không ai phải chỉnh lại hằng số.
 *
 * <p>Chặn trên {@link #MAX} vì khung này là một CÁI ĐIỆN THOẠI: màn hình cao quá thì kéo dài mãi sẽ ra
 * một dải không giống thiết bị nào, và iframe bên trong sẽ dựng Mini App ở một khổ không bao giờ
 * tồn tại trên máy thật. Chặn dưới {@link #MIN} vì thấp hơn nữa thì không còn ra hình điện thoại;
 * màn quá thấp thì thà để trang cuộn một chút.
 */
const WIDTH = 320;
/** 320×860 — cao hơn một chút so với máy thật (390×844 thu về 320 là ~692) nhưng vẫn ra hình thiết bị. */
const MAX = 860;
const MIN = 420;
/**
 * Không chừa thêm nhịp nào: lề dưới của {@code <main>} đã là khoảng thở sẵn. Trừ thêm một con số
 * nữa chỉ làm khung máy thấp đi mà không bù lại điều gì.
 */
const BOTTOM_GAP = 0;

const shell: React.CSSProperties = {
  width: WIDTH,
  borderRadius: 34,
  border: "10px solid #111827",
  overflow: "hidden",
  position: "relative",
  background: "#fff",
  flex: "none",
  boxShadow: "0 8px 28px rgba(0,0,0,.18)",
};

/**
 * Chiều cao vừa đúng khoảng trống còn lại, tính lại khi vùng cuộn hoặc thẻ đổi kích thước.
 *
 * <p>Trả về {@code null} ở lần render đầu: chưa có DOM thì chưa đo được, và đoán bừa một con số rồi
 * sửa ngay sau đó sẽ thành một cú nhảy nhìn thấy được. Người gọi dùng {@link #MIN} trong lúc chờ.
 */
function useFillHeight(
  cardRef: React.RefObject<HTMLDivElement | null>,
  shellRef: React.RefObject<HTMLDivElement | null>,
): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const card = cardRef.current;
    const phone = shellRef.current;
    if (!card || !phone) return;

    // Vùng cuộn là <main> của AppShell (`flex-1 overflow-y-auto`). Không tìm thấy thì rơi về cửa sổ.
    const scroller = card.closest("main");

    const measure = () => {
      /*
       * Đáy VÙNG NỘI DUNG, không phải đáy hộp: `<main>` có `p-4 md:p-6`, và `getBoundingClientRect`
       * tính cả lề đó. Bỏ quên nó thì thẻ tràn vào vùng lề đúng bằng phần chênh lệch và trang cuộn
       * thêm mấy pixel — đo được 8px trước khi sửa, đủ để hiện một thanh cuộn không ai muốn.
       */
      const bottom = scroller
        ? scroller.getBoundingClientRect().bottom -
          parseFloat(getComputedStyle(scroller).paddingBottom || "0")
        : document.documentElement.clientHeight;
      // Phần không phải khung máy trong thẻ: tiêu đề, đoạn mô tả, chú thích, lề. Đo ra thay vì khai
      // sẵn, để thêm bớt nội dung trong thẻ không làm sai chiều cao.
      const chrome = card.offsetHeight - phone.offsetHeight;
      const avail = bottom - card.getBoundingClientRect().top - chrome - BOTTOM_GAP;
      setHeight(Math.round(Math.min(MAX, Math.max(MIN, avail))));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    if (scroller) ro.observe(scroller);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [cardRef, shellRef]);

  return height;
}

export function PhonePreview({ className }: { className?: string }) {
  const q = useQuery({ queryKey: ["miniapp", "preview"], queryFn: previewApi });
  const cardRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const fillHeight = useFillHeight(cardRef, shellRef);

  const data = q.data;
  const b = data?.bundle;
  const tokens = b?.theme?.tokens ?? {};
  const primary = safeColor(tokens.brand ?? b?.theme?.primaryColor, "#0ea5e9");
  const bg = safeColor(tokens.bg, "#f8fafc");
  const surface = safeColor(tokens.surface, "#ffffff");
  const text = safeColor(tokens.ink, "#0f172a");
  // Bộ token không có màu chữ phụ; lấy ink mờ đi để khỏi bịa thêm một token không tồn tại.
  const muted = "rgba(15, 23, 42, .6)";
  const posts = b?.recentPosts ?? [];

  return (
    <Card className={className} ref={cardRef}>
      <CardContent className="p-5">
        <h2 className="text-sm font-semibold text-foreground">Bệnh nhân đang thấy gì</h2>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          {data?.previewUrl
            ? "Chính Mini App đang chạy, đọc nội dung và màu sắc của phòng khám này từ Addy Sites."
            : "Xem trước nội dung và màu sắc Mini App đang đọc từ Addy Sites. Đây là bản mô phỏng để xem nhanh, không phải bản dựng thật trên Zalo."}
        </p>

        {/* Xếp dọc, không phải `flex-wrap`: khối này giờ luôn nằm ở cột hẹp bên phải, nên cạnh
            nhau là điều không bao giờ xảy ra — nói thẳng ra thay vì để wrap quyết định hộ. */}
        <div className="mt-4 flex flex-col items-center gap-4">
          <div ref={shellRef} style={{ ...shell, height: fillHeight ?? MIN }}>
            {q.isLoading ? (
              <div className="space-y-3 p-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : !data?.available ? (
              <div
                style={{ background: bg, color: muted }}
                className="flex h-full items-center justify-center p-6 text-center text-sm"
              >
                {data?.reason ?? "Chưa lấy được nội dung."}
              </div>
            ) : data.previewUrl ? (
              /*
               * Nhúng CHÍNH Mini App, không vẽ lại nó.
               *
               * Chú thích ở đầu file từng nói nhúng app thật là không khả thi, vì bản dựng cục bộ
               * gọi `zmp-sdk` sẽ hỏng ngoài môi trường Zalo. Điều đó không còn đúng: Mini App giờ
               * chạy được trong trình duyệt thường — nó tự mở phiên tài khoản demo khi không xin
               * được token Zalo, bỏ qua quyền camera, và không còn màn hình nào sập vì thiếu
               * salepro. Addy Sites đã nhúng đúng cách này ở Theme Studio.
               *
               * Vì sao đáng đổi: bản vẽ lại chỉ dựng được những gì có người ngồi dựng — hiện là màu
               * và bài viết — nên mỗi lần app đọc thêm một phần của bundle thì khung này lại sai
               * thêm một phần, và không có gì báo. Nhúng app thật thì nó đúng theo app.
               *
               * `key` theo slug: đổi phòng khám là phải nạp lại iframe, vì slug được đọc MỘT LẦN
               * lúc app khởi động.
               */
              <iframe
                key={data.slug}
                src={data.previewUrl}
                title="Xem trước Zalo Mini App"
                style={{ width: "100%", height: "100%", border: 0, display: "block" }}
              />
            ) : (
              <div style={{ background: bg, color: text, height: "100%", overflowY: "auto" }}>
                {/* Đầu app: logo + tên phòng khám trên nền màu chủ đạo — đúng thứ theme.tokens đổi. */}
                <div style={{ background: primary, color: "#fff", padding: "22px 16px 18px" }}>
                  {b?.theme?.logoUrl ? (
                    <img
                      src={b.theme.logoUrl}
                      alt=""
                      style={{ height: 28, marginBottom: 8, objectFit: "contain" }}
                    />
                  ) : null}
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {b?.tenant?.clinicName ?? "Phòng khám"}
                  </div>
                  {b?.tenant?.tagline ? (
                    <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                      {b.tenant.tagline}
                    </div>
                  ) : null}
                </div>

                <div style={{ padding: "14px 12px" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Tin tức</div>
                  {posts.length === 0 ? (
                    <div
                      style={{
                        border: `1px dashed ${muted}`,
                        borderRadius: 8,
                        padding: 14,
                        fontSize: 12,
                        color: muted,
                      }}
                    >
                      Chưa có bài viết nào. Thêm ở Addy Sites → Bài viết.
                    </div>
                  ) : (
                    posts.slice(0, 6).map((p, i) => (
                      <div
                        key={p.slug ?? i}
                        style={{
                          border: "1px solid rgba(0,0,0,.08)",
                          borderRadius: 10,
                          overflow: "hidden",
                          marginBottom: 10,
                          background: surface,
                        }}
                      >
                        {p.coverImageUrl ? (
                          <img
                            src={p.coverImageUrl}
                            alt=""
                            style={{ width: "100%", height: 92, objectFit: "cover", display: "block" }}
                          />
                        ) : null}
                        <div style={{ padding: 10 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                            {p.title ?? "(không có tiêu đề)"}
                          </div>
                          {p.excerpt ? (
                            <div style={{ fontSize: 11, color: muted, marginTop: 4 }}>
                              {p.excerpt}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Nói thẳng phần nào đã lên app, phần nào chưa — nếu không, sửa trang chủ bên Sites rồi
              mở lại đây thấy không đổi thì tưởng hỏng. */}
          <div className="w-full text-xs text-muted-foreground">
            {/*
             * Câu này chỉ còn đúng cho BẢN MÔ PHỎNG. Khi nhúng app thật thì không cần liệt kê phần
             * nào đã nối: app tự hiện đúng thứ nó đọc được, nên nói thêm là nói sai.
             */}
            {!data?.previewUrl ? (
              <p className="mt-0">
                Mini App hiện đọc từ Addy Sites: <b>màu giao diện</b> và <b>bài viết</b>. Các phần
                khác (nội dung trang chủ, trang, dịch vụ, bác sĩ) đã có sẵn trong nội dung dùng
                chung nhưng bản mô phỏng này chưa vẽ, nên sửa cũng chưa hiện lên đây.
              </p>
            ) : null}
            {data?.slug ? (
              <p>
                Đang xem nội dung của: <b>{data.slug}</b>
              </p>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
